/**
 * Dated lease-vs-own comparison — pure module (no React/DOM imports).
 *
 * Conventions:
 * - Dates are ISO calendar dates ("YYYY-MM-DD"), interpreted at UTC midnight
 *   so the arithmetic is timezone-stable.
 * - Discounting uses an ACT/365 year fraction (t = elapsed days / 365), so a
 *   365-day span is exactly one discount period. Effective annual
 *   discounting: DF = 1 / (1 + rate)^t.
 * - Cash-cost sign convention: lease payments, owner costs, selling costs,
 *   debt repayments/interest/fees are POSITIVE (cash required to fund);
 *   terminal value and debt draws are NEGATIVE (inflow / funding source).
 *
 * The primary comparison is UNFINANCED (lease vs own excluding debt) so debt
 * is never double counted with a WACC-derived discount rate; financed equity
 * views come separately from the explicit dated financing flows. There is no
 * legacy developer totalcost or avoided-rent terminal proxy here.
 */
export type DatedFlow = {
  date: string;
  /** Cash cost (positive) or inflow (negative) in input units. */
  amount: number;
  label?: string;
};

/** A recurring owner lifecycle expense line (rates, insurance, ...). */
export type LifecycleCategory = {
  name: string;
  /** Annual scheduling amount for the first schedule year; null = unknown. */
  annualAmount: number | null;
  /** Annual escalation applied per elapsed schedule year. */
  escalationRate: number;
  /** First payment date; defaults to the valuation date. */
  firstPaymentDate?: string;
};

export type OwnerLifecycle = {
  /** Declares that the listed categories are the complete lifecycle set. */
  declaredComplete: boolean;
  categories: LifecycleCategory[];
};

export type FinancingFlows = {
  draws: DatedFlow[];
  repayments: DatedFlow[];
  interestPayments: DatedFlow[];
  fees: DatedFlow[];
};

export type OwnershipInput = {
  /** Carried through untouched; never interpreted or converted. */
  currency?: string;
  valuationDate: string;
  endDate: string;
  annualDiscountRate: number;
  leasePayments: DatedFlow[];
  /** Empty/default uses the resolved dated landlord-rent ledger; non-empty is a documented manual override. */
  leasePaymentSource?: "scenario-rent" | "manual";
  ownerAcquisitionAndBuildCosts: DatedFlow[];
  /** Omitted means recurring lifecycle costs are unknown. */
  ownerLifecycle?: OwnerLifecycle | null;
  /** Asset value at endDate. null = unknown; 0 = explicit zero. */
  terminalValue: number | null;
  /** Costs of selling at endDate. null = unknown; 0 = explicit zero. */
  sellingCosts: number | null;
  financing?: FinancingFlows | null;
};

export type DatedSeries =
  | "lease"
  | "own-capital"
  | "own-lifecycle"
  | "terminal-value"
  | "selling-costs"
  | "financing-draw"
  | "financing-repayment"
  | "financing-interest"
  | "financing-fee";

export type DatedRow = {
  series: DatedSeries;
  date: string;
  label: string;
  amount: number;
  yearsFromValuation: number;
  discountFactor: number;
  pv: number;
  yearIndex: number;
  withinHorizon: boolean;
};

export type AnnualSummary = {
  yearIndex: number;
  calendarYear: string;
  leaseNominal: number;
  leasePv: number;
  ownCostNominal: number;
  ownCostPv: number;
  netOwnNominal: number;
  netOwnPv: number;
  debtServiceNominal: number;
  debtServicePv: number;
  netEquityNominal: number;
  netEquityPv: number;
};

export type OwnershipResult = {
  currency?: string;
  /** Visible rejections: invalid dates/order, nonfinite values, rate <= -1. */
  errors: string[];
  /** Advisory completeness notes (unknown fields, exclusions). */
  issues: string[];
  /** True only when the settled advantage is decided (nothing unknown). */
  complete: boolean;
  /** Why the advantage is not settled (empty when settled). */
  suppressionReasons: string[];
  /**
   * Settled lease-vs-own advantage (PV lease cost − PV own net cost);
   * POSITIVE means ownership is cheaper. null when suppressed.
   */
  advantage: number | null;
  pvLeaseCost: number | null;
  /** PV of own costs net of terminal/sale proceeds (never debt). */
  pvOwnNetCost: number | null;
  /** Own (unfinanced) cash requirement dated on the valuation date. */
  upfrontOwnCash: number | null;
  /** Peak cumulative own net cash requirement over the horizon. */
  peakCumulativeOwnCash: number | null;
  financedEquity: {
    pvEquityNetCost: number | null;
    upfrontEquityCash: number | null;
    peakCumulativeEquityCash: number | null;
    equityAdvantage: number | null;
  } | null;
  datedFlows: DatedRow[];
  annual: AnnualSummary[];
};

const MS_PER_YEAR = 86_400_000 * 365;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function utcTime(iso: string): number | null {
  if (typeof iso !== "string" || !ISO_DATE.test(iso)) return null;
  const mm = iso.slice(5, 7);
  const dd = iso.slice(8, 10);
  if (mm === "00" || mm > "12" || dd === "00" || dd > "31") return null;
  const t = Date.parse(`${iso}T00:00:00Z`);
  if (!Number.isFinite(t)) return null;
  // Reject non-canonical calendar days like 2026-02-30.
  if (new Date(t).toISOString().slice(0, 10) !== iso) return null;
  return t;
}

function anniversaryTime(iso: string, offsetYears: number): number {
  // Same UTC calendar month/day each year; clamp 29 Feb to 28 Feb.
  const y = Number(iso.slice(0, 4)) + offsetYears;
  const md = `${iso.slice(5, 7)}-${iso.slice(8, 10)}`;
  if (md === "02-29" && new Date(Date.UTC(y, 1, 29)).getUTCDate() !== 29) {
    return Date.parse(`${y}-02-28T00:00:00Z`);
  }
  const candidate = Date.parse(`${y}-${md}T00:00:00Z`);
  if (
    Number.isFinite(candidate) &&
    new Date(candidate).toISOString().slice(0, 10) === `${y}-${md}`
  ) {
    return candidate;
  }
  return Date.parse(`${y}-02-28T00:00:00Z`);
}

export function computeDatedOwnership(input: OwnershipInput): OwnershipResult {
  const errors: string[] = [];
  const issues: string[] = [];

  const valT = utcTime(input.valuationDate);
  const endT = utcTime(input.endDate);
  if (valT === null) {
    errors.push(`Invalid valuationDate: ${String(input.valuationDate)}`);
  }
  if (endT === null) {
    errors.push(`Invalid endDate: ${String(input.endDate)}`);
  }
  if (
    !Number.isFinite(input.annualDiscountRate) ||
    input.annualDiscountRate <= -1
  ) {
    errors.push(
      `annualDiscountRate must be finite and greater than -1 (got ${String(input.annualDiscountRate)})`,
    );
  }
  if (valT !== null && endT !== null && endT <= valT) {
    errors.push(
      `endDate (${input.endDate}) must be after valuationDate (${input.valuationDate})`,
    );
  }

  if (errors.length > 0) {
    return {
      currency: input.currency,
      errors,
      issues: [...errors],
      complete: false,
      suppressionReasons: ["input rejected"],
      advantage: null,
      pvLeaseCost: null,
      pvOwnNetCost: null,
      upfrontOwnCash: null,
      peakCumulativeOwnCash: null,
      financedEquity: null,
      datedFlows: [],
      annual: [],
    };
  }

  const val = valT as number;
  const end = endT as number;
  const rate = input.annualDiscountRate;

  // Collect every candidate cash flow with a series label before discounting.
  type Collected = {
    series: DatedSeries;
    date: string;
    amount: number;
    label: string;
  };
  const collected: Collected[] = [];

  const addDated = (
    series: DatedSeries,
    flows: DatedFlow[] | undefined,
    kind: string,
    sign: 1 | -1 = 1,
    preserveSign = false,
  ) => {
    for (const f of flows ?? []) {
      const t = utcTime(f.date);
      if (t === null) {
        errors.push(`Invalid ${kind} date: ${String(f.date)}`);
        continue;
      }
      if (t < val) {
        errors.push(`${kind} dated before valuationDate: ${f.date}`);
        continue;
      }
      if (!Number.isFinite(f.amount)) {
        errors.push(`Nonfinite ${kind} amount on ${f.date}`);
        continue;
      }
      collected.push({
        series,
        date: f.date,
        amount: preserveSign ? sign * f.amount : sign * Math.abs(f.amount),
        label: f.label ?? kind,
      });
    }
  };

  // Lease credits/refunds are valid negative costs. Source-rent resolution can
  // legitimately produce them from a dated rent-credit contribution.
  addDated("lease", input.leasePayments, "lease payment", 1, true);
  addDated(
    "own-capital",
    input.ownerAcquisitionAndBuildCosts,
    "own acquisition/build cost",
  );

  // Terminal: null = unknown (suppresses the advantage, never treated as
  // zero); 0 = explicit zero. Terminal is discounted at the selected endDate
  // and emitted exactly once, as separate signed rows.
  if (input.terminalValue === null) {
    issues.push(
      "terminalValue unknown (null): settled advantage suppressed, missing not treated as zero",
    );
  } else if (!Number.isFinite(input.terminalValue)) {
    errors.push("Nonfinite terminalValue");
  } else if (input.terminalValue < 0) {
    errors.push("terminalValue must be non-negative");
  } else {
    collected.push({
      series: "terminal-value",
      date: input.endDate,
      amount: -Math.abs(input.terminalValue),
      label: "owned asset terminal value (inflow)",
    });
  }
  if (input.sellingCosts === null) {
    issues.push(
      "sellingCosts unknown (null): settled advantage suppressed, missing not treated as zero",
    );
  } else if (!Number.isFinite(input.sellingCosts)) {
    errors.push("Nonfinite sellingCosts");
  } else if (input.sellingCosts < 0) {
    errors.push("sellingCosts must be non-negative");
  } else if (input.sellingCosts !== 0) {
    collected.push({
      series: "selling-costs",
      date: input.endDate,
      amount: Math.abs(input.sellingCosts),
      label: "selling costs at endDate",
    });
  }

  // Recurring owner lifecycle: scheduling amounts escalate per schedule year
  // on each calendar anniversary of the first payment date.
  const lifecycle =
    input.ownerLifecycle === undefined ? undefined : input.ownerLifecycle;
  let lifecycleKnown = false;
  if (lifecycle == null) {
    issues.push(
      "ownerLifecycle not provided: recurring ownership costs unknown, settled advantage suppressed",
    );
  } else {
    if (!lifecycle.declaredComplete) {
      issues.push(
        "ownerLifecycle.declaredComplete is false: expense-category set declared incomplete, settled advantage suppressed",
      );
    }
    let allKnown = lifecycle.categories.length > 0;
    if (lifecycle.categories.length === 0) {
      issues.push(
        "ownerLifecycle.declaredComplete is true with an empty category list",
      );
      allKnown = true;
    }
    for (const cat of lifecycle.categories) {
      if (cat.annualAmount === null) {
        issues.push(
          `ownerLifecycle category "${cat.name}" annualAmount unknown: not treated as zero`,
        );
        allKnown = false;
        continue;
      }
      if (!Number.isFinite(cat.annualAmount)) {
        errors.push(`Nonfinite ownerLifecycle annualAmount for "${cat.name}"`);
        allKnown = false;
        continue;
      }
      if (cat.annualAmount < 0) {
        errors.push(
          `ownerLifecycle annualAmount for "${cat.name}" must be non-negative`,
        );
        allKnown = false;
        continue;
      }
      if (!Number.isFinite(cat.escalationRate)) {
        errors.push(
          `Nonfinite escalationRate for lifecycle category "${cat.name}"`,
        );
        allKnown = false;
        continue;
      }
      if (cat.escalationRate <= -1) {
        errors.push(
          `ownerLifecycle escalationRate for "${cat.name}" must be greater than -1`,
        );
        allKnown = false;
        continue;
      }
      const first = cat.firstPaymentDate ?? input.valuationDate;
      const firstMs = utcTime(first);
      if (firstMs === null || firstMs < val) {
        errors.push(
          `Lifecycle category "${cat.name}" has invalid firstPaymentDate: ${String(first)}`,
        );
        continue;
      }
      for (let k = 0; ; k++) {
        const ms = k === 0 ? firstMs : anniversaryTime(first, k);
        if (ms > end) break;
        const amount = cat.annualAmount * (1 + cat.escalationRate) ** k;
        if (!Number.isFinite(amount)) {
          errors.push(`Nonfinite scheduled lifecycle amount for "${cat.name}"`);
          continue;
        }
        collected.push({
          series: "own-lifecycle",
          date: new Date(ms).toISOString().slice(0, 10),
          amount,
          label: `${cat.name} (schedule year ${k + 1})`,
        });
      }
    }
    lifecycleKnown = allKnown;
  }

  const financing = input.financing ?? null;
  if (financing !== null) {
    addDated("financing-draw", financing.draws, "financing draw", -1);
    addDated(
      "financing-repayment",
      financing.repayments,
      "financing repayment",
      1,
    );
    addDated(
      "financing-interest",
      financing.interestPayments,
      "financing interest",
    );
    addDated("financing-fee", financing.fees, "financing fee");
  }

  // Discount each flow at its exact year fraction. Post-horizon flows are
  // preserved with their original dated amounts (withinHorizon false) but
  // excluded from every PV and requirement.
  const datedFlows: DatedRow[] = [];
  for (const c of collected) {
    const ms = utcTime(c.date) as number;
    const withinHorizon = ms <= end;
    if (!withinHorizon) {
      issues.push(
        `${c.series} on ${c.date} is after endDate and is excluded from all PVs (original dated amount preserved)`,
      );
    }
    const t = (ms - val) / MS_PER_YEAR;
    const df = 1 / (1 + rate) ** t;
    datedFlows.push({
      series: c.series,
      date: c.date,
      label: c.label,
      amount: c.amount,
      yearsFromValuation: t,
      discountFactor: df,
      pv: c.amount * df,
      yearIndex: Math.floor(t),
      withinHorizon,
    });
  }

  const suppressionReasons: string[] = [];
  if (input.terminalValue === null) {
    suppressionReasons.push("terminalValue unknown");
  }
  if (input.sellingCosts === null) {
    suppressionReasons.push("sellingCosts unknown");
  }
  if (lifecycle == null) {
    suppressionReasons.push("ownerLifecycle not provided");
  } else if (!lifecycle.declaredComplete) {
    suppressionReasons.push("ownerLifecycle categories declared incomplete");
  } else if (!lifecycleKnown) {
    suppressionReasons.push("one or more lifecycle category amounts unknown");
  }

  const inHorizon = datedFlows.filter((r) => r.withinHorizon);
  const pvOf = (series: DatedSeries) =>
    inHorizon.filter((r) => r.series === series).reduce((a, r) => a + r.pv, 0);
  const pvLeaseCost = pvOf("lease");
  // Own net cost = capital + lifecycle + selling costs + signed terminal inflow
  // (each counted exactly once; the terminal row exists only once).
  const pvOwnNetCost =
    pvOf("own-capital") +
    pvOf("own-lifecycle") +
    pvOf("selling-costs") +
    pvOf("terminal-value");

  // Own (unfinanced) cumulative cash requirement, in date order: costs are
  // positive needs; the terminal inflow reduces the requirement.
  const byDateAsc = (a: DatedRow, b: DatedRow) =>
    a.date === b.date ? 0 : a.date < b.date ? -1 : 1;
  const ownRows = inHorizon
    .filter((r) => r.series !== "lease" && !r.series.startsWith("financing-"))
    .sort(byDateAsc);
  const ownByDate = new Map<string, number>();
  for (const r of ownRows)
    ownByDate.set(r.date, (ownByDate.get(r.date) ?? 0) + r.amount);
  let cum = 0;
  let peakCumulativeOwnCash = 0;
  let upfrontOwnCash = 0;
  for (const date of [...ownByDate.keys()].sort()) {
    const amount = ownByDate.get(date) ?? 0;
    cum += amount;
    if (date === input.valuationDate) upfrontOwnCash += amount;
    peakCumulativeOwnCash = Math.max(peakCumulativeOwnCash, cum);
  }

  // Annual discounted summaries, 0-based year indices from the valuation date.
  const annualMap = new Map<number, AnnualSummary>();
  const equityRowsByYear = new Map<number, DatedRow[]>();
  const equityDrawsByYear = new Map<number, DatedRow[]>();
  for (const r of inHorizon) {
    let s = annualMap.get(r.yearIndex);
    if (!s) {
      s = {
        yearIndex: r.yearIndex,
        calendarYear: r.date.slice(0, 4),
        leaseNominal: 0,
        leasePv: 0,
        ownCostNominal: 0,
        ownCostPv: 0,
        netOwnNominal: 0,
        netOwnPv: 0,
        debtServiceNominal: 0,
        debtServicePv: 0,
        netEquityNominal: 0,
        netEquityPv: 0,
      };
      annualMap.set(r.yearIndex, s);
    }
    if (r.series !== "lease" && !r.series.startsWith("financing-")) {
      s.netOwnNominal += r.amount;
      s.netOwnPv += r.pv;
    }
    if (r.series === "lease") {
      s.leaseNominal += r.amount;
      s.leasePv += r.pv;
    }
    if (
      r.series === "own-capital" ||
      r.series === "own-lifecycle" ||
      r.series === "selling-costs"
    ) {
      s.ownCostNominal += r.amount;
      s.ownCostPv += r.pv;
    }
    if (
      r.series === "financing-repayment" ||
      r.series === "financing-interest" ||
      r.series === "financing-fee"
    ) {
      s.debtServiceNominal += r.amount;
      s.debtServicePv += r.pv;
    }
    if (r.series !== "lease" && !r.series.startsWith("financing-")) {
      const eq = equityRowsByYear.get(r.yearIndex) ?? [];
      eq.push(r);
      equityRowsByYear.set(r.yearIndex, eq);
    }
    if (r.series === "financing-draw") {
      const draws = equityDrawsByYear.get(r.yearIndex) ?? [];
      draws.push(r);
      equityDrawsByYear.set(r.yearIndex, draws);
    }
  }

  // Financed equity view (separate from the primary unfinanced comparison):
  // equity = own costs + selling costs − terminal − draws + debt service.
  let financedEquity: OwnershipResult["financedEquity"] = null;
  if (financing !== null) {
    for (const s of annualMap.values()) {
      const eqRows = equityRowsByYear.get(s.yearIndex) ?? [];
      const drawRows = equityDrawsByYear.get(s.yearIndex) ?? [];
      s.netEquityNominal =
        eqRows.reduce((a, r) => a + r.amount, 0) +
        drawRows.reduce((a, r) => a + r.amount, 0) +
        s.debtServiceNominal;
      s.netEquityPv =
        eqRows.reduce((a, r) => a + r.pv, 0) +
        drawRows.reduce((a, r) => a + r.pv, 0) +
        s.debtServicePv;
    }
    const pvEquityNetCost =
      pvOwnNetCost +
      (pvOf("financing-repayment") +
        pvOf("financing-interest") +
        pvOf("financing-fee")) +
      pvOf("financing-draw");
    let upfrontEquityCash = 0;
    let peakEquity = 0;
    let equityCum = 0;
    const eqChrono = inHorizon
      .filter((r) => r.series !== "lease")
      .sort(byDateAsc);
    const equityByDate = new Map<string, number>();
    for (const r of eqChrono)
      equityByDate.set(r.date, (equityByDate.get(r.date) ?? 0) + r.amount);
    for (const date of [...equityByDate.keys()].sort()) {
      equityCum += equityByDate.get(date) ?? 0;
      if (date === input.valuationDate) upfrontEquityCash = equityCum;
      peakEquity = Math.max(peakEquity, equityCum);
    }
    financedEquity = {
      pvEquityNetCost,
      upfrontEquityCash,
      peakCumulativeEquityCash: peakEquity,
      equityAdvantage:
        errors.length > 0 || suppressionReasons.length > 0
          ? null
          : pvLeaseCost - pvEquityNetCost,
    };
  }

  const settled = errors.length === 0 && suppressionReasons.length === 0;
  const advantage = settled ? pvLeaseCost - pvOwnNetCost : null;

  return {
    currency: input.currency,
    errors,
    issues,
    complete: settled,
    suppressionReasons,
    advantage,
    pvLeaseCost,
    pvOwnNetCost,
    upfrontOwnCash,
    peakCumulativeOwnCash,
    financedEquity,
    datedFlows,
    annual: [...annualMap.values()].sort((a, b) => a.yearIndex - b.yearIndex),
  };
}
